import * as React from "react";
import { cn } from "@/lib/utils";
import { useColorsSettings } from "@/store/settings";
import { TEXT_COLORS } from "@/utils";

import type { Word } from "@/lib/types";

// Word-link highlight shapes. The shape carries the memorisation method's
// meaning, so square stays nearly square and circle stays an ellipse. Fills
// multiply into the paper (the tint reads as ink-on-paper, never a sticker);
// outlines stay flat. data-hl lets print reset the blend.
const SHAPE_CLASSES: Record<string, string> = {
  SQUARE: "rounded-[2px] mix-blend-multiply",
  "SQUARE-BORDER-ONLY": "rounded-[2px] border-2",
  CIRCLE: "rounded-[50%] mix-blend-multiply",
  "CIRCLE-BORDER-ONLY": "rounded-[50%] border-2",
};

interface WordGlyphProps {
  word: Word;
  /** Classes for the glyph; the size otherwise inherits (text-mushaf on the page). */
  className?: string;
  /** Draw the word's highlight (the "Poveznice" setting). */
  showColors: boolean;
}

/**
 * A mushaf word with its highlight, without a store subscription: lists of
 * words (a page has ~150) pass the setting down, so unrelated settings
 * changes (the last page is saved on every page turn) don't re-render them.
 */
export const WordGlyph = React.memo<WordGlyphProps>(({ word, className, showColors }) => {
  const color = showColors && word.color ? TEXT_COLORS.get(word.color) : undefined;
  const shape = color && word.shape ? SHAPE_CLASSES[word.shape] : undefined;
  const outline = word.shape?.endsWith("BORDER-ONLY");

  return (
    <span className="relative inline-block select-none rounded-[3px]">
      <span className={cn("relative z-10", className)}>{word.text}</span>
      {shape ? (
        <span
          data-hl
          aria-hidden
          className={cn("absolute inset-0 z-0", shape)}
          style={
            outline
              ? // The pale fill tones vanish as a 2px line on cream; darken them.
                { borderColor: `color-mix(in oklab, ${color}, var(--color-ink-muted) 40%)` }
              : { backgroundColor: color }
          }
        />
      ) : null}
    </span>
  );
});
WordGlyph.displayName = "WordGlyph";

interface ColoredWordProps {
  word: Word;
  className?: string;
  disableColors?: boolean;
}

/** A single word that follows the colours setting by itself. */
const ColoredWord: React.FC<ColoredWordProps> = ({ word, className, disableColors = false }) => {
  const { colors } = useColorsSettings();
  return <WordGlyph word={word} className={className} showColors={colors && !disableColors} />;
};

export default React.memo(ColoredWord);
